import { type Registry as Services, service } from '@ember/service';
import Model, {
  attr,
  belongsTo,
  type HasMany,
  hasMany,
} from '@ember-data/model';
import { tracked } from '@glimmer/tracking';
import { Type } from '@warp-drive/core-types/symbols';
import type {
  TotalRevenueHotel,
  TotalRevenueReferenceItem,
} from 'lumen/models';
import safeDivide from 'lumen/utils/numeric/safe-divide';

export default class TotalRevenueItem extends Model {
  [Type] = 'total-revenue/item' as const;

  @belongsTo('total-revenue/hotel', { async: false, inverse: null })
  declare subscription?: TotalRevenueHotel;

  @hasMany('total-revenue/reference-item', {
    async: false,
    inverse: 'totalRevenueItem',
  })
  declare referenceValues: HasMany<TotalRevenueReferenceItem>;

  @attr('number') declare nonRoomPremium: number;
  @attr('number') declare totalNonRoomRevenue: number;
  @attr('number') declare totalRevenue: number;
  @attr('number') declare totalRoomRevenue: number;

  @service declare intl: Services['intl'];
  @service('total-revenue')
  declare totalRevenueService: Services['total-revenue'];

  @tracked capacity?: number;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get displayName() {
    return this.subscription?.name;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedNonRoomPremium() {
    return this.totalRevenueService.formatValue(this.nonRoomPremium);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalNonRoomRevenue() {
    return this.totalRevenueService.formatValue(this.totalNonRoomRevenueOrPAR);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalNonRoomRevenuePercentage() {
    return this.formatPercentage(this.totalNonRoomRevenuePercentage);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalRevenue() {
    return this.totalRevenueService.formatValue(this.totalRevenueOrPAR);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalRoomRevenue() {
    return this.totalRevenueService.formatValue(this.totalRoomRevenueOrPAR);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedTotalRoomRevenuePercentage() {
    return this.formatPercentage(this.totalRoomRevenuePercentage);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sortedReferenceValues() {
    return this.totalRevenueService.sortReferenceValues([
      ...this.referenceValues,
    ]);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalNonRoomRevenueOrPAR() {
    return this.totalRevenueService.valueOrPAR(
      this.totalNonRoomRevenue,
      this.capacity,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalNonRoomRevenuePercentage() {
    return safeDivide(this.totalNonRoomRevenue, this.totalRevenue);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRevenueOrPAR() {
    return this.totalRevenueService.valueOrPAR(
      this.totalRevenue,
      this.capacity,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRoomRevenueOrPAR() {
    return this.totalRevenueService.valueOrPAR(
      this.totalRoomRevenue,
      this.capacity,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get totalRoomRevenuePercentage() {
    return safeDivide(this.totalRoomRevenue, this.totalRevenue);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  formatPercentage(value: number | null) {
    if (value === null) {
      return this.intl.t('general.missing');
    }

    return this.intl.formatNumber(value, {
      style: 'percent',
    });
  }
}
